/**
 * FedRAMP KSI readiness — V98-5-8 (FedRAMP Operate tab, once certified;
 * glance-first). V98-5 review:
 *
 *   T5-H1  the shared readiness card (ReadinessHero — the same one Prepare
 *          uses): big %, labelled segments (gaps hatched), "N to go", and ONE
 *          filled primary "Fix N gaps →" that jumps to the gap list; each gap
 *          gets the action its status needs ("No control" → Map a control in
 *          Prepare; otherwise → Automate). "N validation overdue" is a link.
 *   T5-B6  persistent validation is one dim line ("Last validated Sep 30 ·
 *          automated every 7 days · attested every 3 months"); rule codes
 *          never lead — the title comes first, the KSI code after it, dimmed.
 *   T5-B5  every count is a server total (counts.total, automated.total −
 *          automated), never rows.length.
 *
 *   V109-7-2 one row per class (A / B / C): automated methods against the class minimum; Class C
 *          is always a gap with its count (LM-8 launch claim is A/B). DS Chip + existing gp-* rows.
 *
 * Reads: GET /programs/fedramp-cr26/ksi (any member).
 */
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Spinner } from '@aigovops/design-system';
import type { KsiClassAutomation, KsiRow } from '@aigovops/core';
import { getKsiReadiness } from '../../lib/cert-ops-api';
import { fmtDate } from '../../lib/cert-ops-model';
import { Chip, useFocusAfter } from '../today/shared';
import { ReadinessHero } from './ReadinessHero';
import '../../pages/programs.css';
import '../../pages/cert-ops.css';

const STATUS: Record<KsiRow['status'], { text: string; tone: 'ok' | 'red' | 'neutral' }> = {
  met: { text: 'Met', tone: 'ok' }, partial: { text: 'Partial', tone: 'neutral' }, gap: { text: 'Gap', tone: 'red' }, unmapped: { text: 'No control', tone: 'red' },
};

/**
 * V109-7-2 (LM-8): automated validation per class. The launch claim is Class A/B; Class C always
 * reads as a gap with its count — never "Met", even when every KSI reaches its minimum.
 */
const CLASS_CHIP: Record<KsiClassAutomation['status'], { text: string; tone: 'ok' | 'red' | 'amber' | 'neutral' }> = {
  met: { text: 'Met', tone: 'ok' }, gap: { text: 'Gap', tone: 'amber' }, blocker: { text: 'Gap', tone: 'red' },
  pending: { text: 'Not set yet', tone: 'neutral' }, not_claimed: { text: 'Gap', tone: 'amber' },
};

function classLine(c: KsiClassAutomation): string {
  const per = c.minimum === null ? null : `${c.minimum} automated method${c.minimum === 1 ? '' : 's'} per KSI`;
  if (c.status === 'pending') return 'Waiting for the FedRAMP rules approval';
  if (c.status === 'not_claimed') return `${c.short.length} of ${c.ksis} KSIs below ${per ?? 'the minimum'} · not claimed`;
  if (c.status === 'met') return per ? `${c.ksis} of ${c.ksis} KSIs have ${per}` : `${c.ksis} KSIs`;
  return `${c.short.length} of ${c.ksis} KSIs below ${per}`;
}

function ClassAutomation({ classes, prefix }: { classes: KsiClassAutomation[]; prefix: string }) {
  if (!classes.length) return null;
  return (
    <section className="gp-sec" aria-labelledby="ksi-class-h" data-part="ksi-class-automation">
      <h4 id="ksi-class-h" className="gp-sec-h">Automated validation by class</h4>
      <ul className="gp-owed-list" aria-labelledby="ksi-class-h">
        {classes.map((c) => (
          <li key={c.class} className="gp-ow" data-class={c.class} data-status={c.status}>
            <span className="gp-ow-main">
              <span className="gp-ow-line"><span className="gp-ow-t">{`Class ${c.class}`}</span></span>
              <span className="gp-ow-meta"><Chip text={CLASS_CHIP[c.status].text} tone={CLASS_CHIP[c.status].tone} /><span>{classLine(c)}</span></span>
            </span>
            {c.status === 'pending'
              ? <span className="gp-ow-act"><Link className="gp-linkbtn gp-linkbtn--compact" to={`${prefix}/queue`} aria-label={`Open the review queue · Class ${c.class}`}>Review queue</Link></span>
              : c.short.length
                ? <span className="gp-ow-act"><Link className="gp-linkbtn gp-linkbtn--compact" to={`${prefix}/settings/integrations`} aria-label={`Automate · Class ${c.class}`}>Automate</Link></span>
                : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

export function KsiReadinessPanel({ slug, prefix }: { slug: string; prefix: string }) {
  const q = useQuery({ queryKey: ['ksi', slug], queryFn: () => getKsiReadiness(slug, 'fedramp-cr26'), retry: false, staleTime: 60_000 });
  const [all, setAll] = useState(false);
  const gapsRef = useRef<HTMLHeadingElement>(null);
  const focusLater = useFocusAfter();
  if (q.isLoading) return <Spinner label="Loading KSI readiness" />;
  if (q.error || !q.data) return <Alert variant="error"><p>KSI readiness could not load.</p><Button onClick={() => void q.refetch()}>Retry</Button></Alert>;
  const k = q.data.ksi;
  const c = k.counts;
  const v = k.validation;
  const manual = k.automated.total - k.automated.automated;
  const gaps = k.rows.filter((r) => r.status === 'gap' || r.status === 'unmapped');
  const firstOverdue = k.rows.find((r) => r.validation_overdue) ?? null;
  const showOverdue = () => { setAll(true); focusLater(firstOverdue ? `ksi-row-${firstOverdue.requirement_id}` : 'ksi-all'); };
  const action = (r: KsiRow) => r.status === 'unmapped'
    ? <Link className="gp-linkbtn gp-linkbtn--compact" to={`${prefix}/programs/fedramp-cr26/prepare`} aria-label={`Map a control · ${r.title}`}>Map a control</Link>
    : <Link className="gp-linkbtn gp-linkbtn--compact" to={`${prefix}/settings/integrations`} aria-label={`Automate · ${r.title}`}>Automate</Link>;
  return (
    <section className="gp-sec" aria-label="KSI readiness" data-part="ksi-readiness">
      <ReadinessHero id="ksi-ops-h" label={`KSI readiness${k.certification_class ? ` · Class ${k.certification_class}` : ''}`} pct={k.readiness_pct}
        split={c} noun={['KSI', 'KSIs']} by={null} daysLeft={null}
        action={gaps.length || c.gaps
          ? <Button variant="primary" onClick={() => { gapsRef.current?.scrollIntoView?.({ block: 'start' }); gapsRef.current?.focus(); }}>{`Fix ${c.gaps} gap${c.gaps === 1 ? '' : 's'}`} <span aria-hidden="true">→</span></Button>
          : <span className="gp-dim">No KSI gaps.</span>}>
        <p className="gp-counts" data-part="ksi-automation">
          <span>Automated validation <b>{`${k.automated.automated} of ${k.automated.total}`}</b></span>
          {v.overdue ? <span><button type="button" className="gp-textlink" onClick={showOverdue}>{`${v.overdue} validation${v.overdue === 1 ? '' : 's'} overdue`}</button></span> : null}
        </p>
      </ReadinessHero>

      {v.blocked_reason ? (
        <div className="gp-state gp-state--warn" data-part="ksi-validation">
          <strong>Persistent validation paused</strong>
          <span>{v.blocked_reason} <Link to={`${prefix}/queue`}>Open the review queue</Link></span>
        </div>
      ) : (
        <p className="gp-dim" data-part="ksi-validation" title={`${v.cadence.machine.rule_id} · ${v.cadence.non_machine.rule_id}`}>
          {[v.last_run_at ? `Last validated ${fmtDate(v.last_run_at)}` : 'Not validated yet', v.cadence.machine_every ? `automated ${v.cadence.machine_every}` : null,
            v.cadence.non_machine_every ? `attested ${v.cadence.non_machine_every}` : null].filter(Boolean).join(' · ')}
        </p>
      )}

      <ClassAutomation classes={k.class_automation ?? []} prefix={prefix} />

      {gaps.length ? (
        <section className="gp-sec" aria-labelledby="ksi-gaps-h">
          <h4 id="ksi-gaps-h" className="gp-sec-h" ref={gapsRef} tabIndex={-1}>{`KSI gaps · ${c.gaps}`}</h4>
          <ul className="gp-owed-list" aria-labelledby="ksi-gaps-h">
            {gaps.map((r) => (
              <li key={r.requirement_id} className="gp-ow">
                <span className="gp-ow-main">
                  <span className="gp-ow-line"><span className="gp-ow-t">{r.title}</span><span className="gp-dim">{r.ref}</span></span>
                  <span className="gp-ow-meta"><Chip text={STATUS[r.status].text} tone={STATUS[r.status].tone} /><span>{r.proven_by === 'attested' ? 'Attested today' : r.automated ? 'Automated' : 'No evidence yet'}</span></span>
                </span>
                <span className="gp-ow-act">{action(r)}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {manual > 0 ? <p className="gp-dim">{`Without automated validation · ${manual} of ${k.automated.total}`}</p> : null}
      <div className="gp-actions"><Button variant="ghost" aria-expanded={all} aria-controls="ksi-all" onClick={() => setAll((x) => !x)}>{all ? 'Hide all KSIs' : `Show all ${c.total} KSIs`}</Button></div>
      {all ? (
        <ul className="gp-owed-list" id="ksi-all" aria-label="All KSIs" tabIndex={-1}>
          {k.rows.map((r) => (
            <li key={r.requirement_id} className="gp-ow" data-ksi={r.ref} id={`ksi-row-${r.requirement_id}`} tabIndex={-1}>
              <span className="gp-ow-main">
                <span className="gp-ow-line"><span className="gp-ow-t">{r.title}</span><span className="gp-dim">{r.ref}</span></span>
                <span className="gp-ow-meta">
                  <Chip text={STATUS[r.status].text} tone={STATUS[r.status].tone} />
                  <span>{r.automated ? 'Automated' : r.proven_by === 'attested' ? 'Attested' : 'No evidence'}</span>
                  <span>{r.latest_run ? `Last run ${fmtDate(r.latest_run.completed_at)}` : 'Never run'}</span>
                  {r.next_due_at ? <span>{`${r.validation_overdue ? 'Overdue since' : 'Next'} ${fmtDate(r.next_due_at)}`}</span> : null}
                </span>
              </span>
              {r.status !== 'met' ? <span className="gp-ow-act">{action(r)}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
